// src/lib/inviteToken.js
// Issue #547, Session 79.14f, gate decisions D1 and D3.
// Gate: docs/gates/Stede_InviteLink_Gate_547.html
//
// ── WHAT THIS IS FOR, in plain words ─────────────────────────────────────────
//
// A new hire's invite used to point at Supabase, and merely OPENING it spent
// the one-time code — so a colleague's browser, or a mail scanner checking the
// message for malware, could use it up before the person it was meant for ever
// pressed anything. One person at D&R was locked out for thirteen days.
//
// The invite now points at Stede and carries its code AFTER THE `#`.
//
// ── WHY AFTER THE `#`, WHICH IS THE WHOLE FIX (D1) ───────────────────────────
//
// A browser NEVER transmits the part of an address after the `#` to a server.
// So a mail scanner fetching `getstede.com/set-password#type=recovery&t=ABC`
// asks the server for `/set-password` and nothing else — it receives a page
// with no code in it, and there is nothing for it to spend. The code only ever
// exists inside the real recipient's browser.
//
// This came out of the research rather than the design. The plan going in was
// Supabase's documented pattern — a button that redeems the code — which is
// D2 and still ships. It defeats a scanner that FETCHES and then declines to
// press. Putting the code in the fragment is stronger because the scanner never
// receives the code at all. The gate takes both.
//
// ── AND WHY NOT A QUERY STRING, WHICH IS A TRAP RATHER THAN A PREFERENCE (D3)
//
// `?token=…` would be sent to the server, which defeats the point. It is also
// specifically broken here: #126's finding 2 established that Stede's sign-in
// round-trip keeps `from.pathname` and THROWS THE QUERY STRING AWAY. Anyone not
// already signed in would lose the code — and that is every single person this
// exists for.
//
// A path segment (`/set-password/ABC`) survives sign-in and is what
// `/speak-up/t/:token` already does, but it is still sent to the server. The
// fragment is the only form a scanner cannot see.

// The recovery code carried by an invite link, or null when there is none.
//
// Returns null — never a partial answer — for anything that is not one of our
// own invite links, so a caller can treat a non-null result as "this is an
// invite waiting to be redeemed" without a second check.
export function readInviteToken(hash) {
  const raw = String(hash == null ? '' : hash).replace(/^#/, '');
  if (!raw) return null;

  let params;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return null;
  }

  const tokenHash = params.get('t');
  if (!tokenHash) return null;

  // An `access_token` in the fragment is Supabase's OWN shape — the link has
  // ALREADY been redeemed and a session exists. That is the pre-#547 flow and
  // every invite sent before this shipped, which D6 keeps working. It must not
  // be mistaken for a code awaiting redemption, or the page would offer a
  // button that redeems nothing.
  if (params.get('access_token')) return null;

  // `type` decides what Supabase is asked to verify. Only recovery is issued by
  // `create-user.js` today; anything else is refused rather than guessed at,
  // because passing the wrong type to verifyOtp burns the code for nothing.
  const type = params.get('type') || 'recovery';
  if (type !== 'recovery') return null;

  return { tokenHash, type };
}

// The address `create-user.js` mails. Built here so the two halves of the
// contract — what is written and what is read — cannot drift apart, which is
// this project's most-repeated defect shape.
//
// NOTE the argument order: `type` first in the fragment, because AuthContext
// and SetPasswordPage both look for the literal `type=recovery` to decide a
// link is a recovery at all, and they read the fragment as a STRING before any
// parsing happens.
export function inviteLinkFor(origin, tokenHash) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}/set-password#type=recovery&t=${encodeURIComponent(tokenHash)}`;
}
